import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Type, Image, Link as LinkIcon, Mail, Phone, Plus, Trash2, Save, RefreshCw, Eye, EyeOff, X, Share2, List
} from "lucide-react";



const SERVER_URL = "https://allinoneai-d1a5a1b54c64.herokuapp.com";

const AdminFooter = () => {
  const [data, setData] = useState({
    topShape: "",
    logo: "",
    aboutText: "",
    socialLinks: [],
    quickLinks: { title: "", links: [] },
    contactInfo: { title: "", emails: [], phones: [] },
    copyrightText: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/footer`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Handle Simple Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  // Handle Nested Object Change
  const handleNestedChange = (category, field, value) => {
    setData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // --- List Handlers (Social, QuickLinks, Contact) ---
  
  // Social Links
  const addSocialLink = () => {
    const newLink = { id: Date.now().toString(), platform: "", url: "", icon: "" };
    setData(prev => ({ ...prev, socialLinks: [...prev.socialLinks, newLink] }));
  };
  const updateSocialLink = (index, field, value) => {
    const newLinks = [...data.socialLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setData({ ...data, socialLinks: newLinks });
  };
  const removeSocialLink = (index) => {
    const newLinks = data.socialLinks.filter((_, i) => i !== index);
    setData({ ...data, socialLinks: newLinks });
  };

  // Quick Links
  const addQuickLink = () => {
    const newLink = { id: Date.now().toString(), label: "", url: "" };
    setData(prev => ({
      ...prev,
      quickLinks: {
        ...prev.quickLinks,
        links: [...prev.quickLinks.links, newLink]
      }
    }));
  };
  const updateQuickLink = (index, field, value) => {
    const newLinks = [...data.quickLinks.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setData({ ...data, quickLinks: { ...data.quickLinks, links: newLinks } });
  };
  const removeQuickLink = (index) => {
    const newLinks = data.quickLinks.links.filter((_, i) => i !== index);
    setData({ ...data, quickLinks: { ...data.quickLinks, links: newLinks } });
  };

  // Contact Info (Emails & Phones)
  const addContactItem = (type) => {
    const newItem = "";
    setData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [type]: [...prev.contactInfo[type], newItem]
      }
    }));
  };
  const updateContactItem = (type, index, value) => {
    const newList = [...data.contactInfo[type]];
    newList[index] = value;
    setData({ ...data, contactInfo: { ...data.contactInfo, [type]: newList } });
  };
  const removeContactItem = (type, index) => {
    const newList = data.contactInfo[type].filter((_, i) => i !== index);
    setData({ ...data, contactInfo: { ...data.contactInfo, [type]: newList } });
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/footer`, data);
      showMessage("Footer updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default settings?")) return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/footer/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Reset successful", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/footer/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(`Footer ${newStatus ? "activated" : "deactivated"}`, "success");
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Footer Section Management</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${data.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={toggleStatus}
            >
                {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                {data.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving}>
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* Basic Settings */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Image size={20} /> Basic Settings
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-12 mb-3">
                            <label className="form-label small fw-bold">Logo URL</label>
                            <input
                              type="text"
                              name="logo"
                              className="form-control"
                              value={data.logo || ""}
                              onChange={handleChange}
                            />
                        </div>
                       
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">About Text</label>
                        <textarea
                          name="aboutText"
                          className="form-control"
                          rows="3"
                          value={data.aboutText || ""}
                          onChange={handleChange}
                        />
                    </div>
                    <div className="mb-0">
                        <label className="form-label small fw-bold">Copyright Text (HTML allowed)</label>
                        <input
                          type="text"
                          name="copyrightText"
                          className="form-control"
                          value={data.copyrightText || ""}
                          onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <Share2 size={20} /> Social Links
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addSocialLink}>
                        <Plus size={16} /> Add Social Link
                    </button>
                </div>
                <div className="card-body">
                    {data.socialLinks?.map((link, index) => (
                        <div key={link.id || index} className="d-flex gap-2 mb-3 align-items-center">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Icon Class (e.g. fab fa-facebook)"
                              value={link.icon || ""}
                              onChange={(e) => updateSocialLink(index, 'icon', e.target.value)}
                              style={{ maxWidth: '250px' }}
                            />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="URL"
                              value={link.url || ""}
                              onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                            />
                            <button type="button" className="btn btn-outline-danger d-flex align-items-center justify-content-center" onClick={() => removeSocialLink(index)}>
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}
                    {(!data.socialLinks || data.socialLinks.length === 0) && <p className="text-muted small mb-0">No social links added.</p>}
                </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <List size={20} /> Quick Links
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addQuickLink}>
                        <Plus size={16} /> Add Quick Link
                    </button>
                </div>
                <div className="card-body">
                    <div className="mb-4 pb-3 border-bottom">
                        <label className="form-label small fw-bold">Section Title</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Section Title"
                          value={data.quickLinks?.title || ""}
                          onChange={(e) => handleNestedChange('quickLinks', 'title', e.target.value)}
                        />
                    </div>
                    {data.quickLinks?.links?.map((link, index) => (
                        <div key={link.id || index} className="d-flex gap-2 mb-3 align-items-center">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Label"
                              value={link.label || ""}
                              onChange={(e) => updateQuickLink(index, 'label', e.target.value)}
                              style={{ maxWidth: '250px' }}
                            />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="URL"
                              value={link.url || ""}
                              onChange={(e) => updateQuickLink(index, 'url', e.target.value)}
                            />
                            <button type="button" className="btn btn-outline-danger d-flex align-items-center justify-content-center" onClick={() => removeQuickLink(index)}>
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}
                    {(!data.quickLinks?.links || data.quickLinks.links.length === 0) && <p className="text-muted small mb-0">No quick links added.</p>}
                </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Mail size={20} /> Contact Info
                </div>
                <div className="card-body">
                    <div className="mb-4 pb-3 border-bottom">
                        <label className="form-label small fw-bold">Section Title</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Section Title"
                          value={data.contactInfo?.title || ""}
                          onChange={(e) => handleNestedChange('contactInfo', 'title', e.target.value)}
                        />
                    </div>
                    
                    {/* Emails */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label small fw-bold mb-0">Emails</label>
                            <button type="button" className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={() => addContactItem('emails')}>
                                <Plus size={14} /> Add Email
                            </button>
                        </div>
                        {data.contactInfo?.emails?.map((email, index) => (
                            <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                                <div className="input-group">
                                    <span className="input-group-text"><Mail size={16} /></span>
                                    <input
                                      type="email"
                                      className="form-control"
                                      placeholder="Email Address"
                                      value={email || ""}
                                      onChange={(e) => updateContactItem('emails', index, e.target.value)}
                                    />
                                </div>
                                <button type="button" className="btn btn-outline-danger d-flex align-items-center justify-content-center" onClick={() => removeContactItem('emails', index)}>
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Phones */}
                    <div className="mb-0">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label small fw-bold mb-0">Phones</label>
                            <button type="button" className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={() => addContactItem('phones')}>
                                <Plus size={14} /> Add Phone
                            </button>
                        </div>
                        {data.contactInfo?.phones?.map((phone, index) => (
                            <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                                <div className="input-group">
                                    <span className="input-group-text"><Phone size={16} /></span>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Phone Number"
                                      value={phone || ""}
                                      onChange={(e) => updateContactItem('phones', index, e.target.value)}
                                    />
                                </div>
                                <button type="button" className="btn btn-outline-danger d-flex align-items-center justify-content-center" onClick={() => removeContactItem('phones', index)}>
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminFooter;